// \src\pages\produtos\page.tsx

import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useAdmin } from "@/context/AdminContext";
import PromoCarouselHome from "@/pages/home/components/PromoCarouselHome";

function getLocalDateString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function isProductVisible(product: {
  active: boolean;
  activeUntil: string;
}): boolean {
  if (!product.active) {
    return false;
  }

  if (!product.activeUntil) {
    return true;
  }

  return product.activeUntil >= getLocalDateString();
}

export default function Produtos() {
  const { data } = useAdmin();
  const products = data.products.filter(isProductVisible);
  const categories = data.categories;
  const [activeCategory, setActiveCategory] = useState<number | null>(null);
  const [activeSubcategory, setActiveSubcategory] = useState<number | null>(null);
  const [expandedCategory, setExpandedCategory] = useState<number | null>(null);
  const [search, setSearch] = useState("");

  const filteredProducts = products.filter((p) => {
    const matchesCat = activeCategory ? p.category === activeCategory : true;
    const matchesSub = activeSubcategory ? p.subcategoryId === activeSubcategory : true;
    const query = search.trim().toLowerCase();
    const matchesSearch = query
      ? [
          p.name,
          p.code,
          p.description,
          p.brand,
          ...(p.specs || []),
        ].some(value => String(value || "").toLowerCase().includes(query))
      : true;
    return matchesCat && matchesSub && matchesSearch;
  });

  const handleCategoryClick = (catId: number) => {
    if (activeCategory === catId) {
      setActiveCategory(null);
      setActiveSubcategory(null);
      setExpandedCategory(null);
    } else {
      setActiveCategory(catId);
      setActiveSubcategory(null);
      setExpandedCategory(catId);
    }
  };

  const toggleExpand = (catId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedCategory(expandedCategory === catId ? null : catId);
  };

  return (
    <>
      <Navbar />
      <main className="pt-20 min-h-screen bg-background-50">
        <section className="bg-background-200 border-b border-background-300 py-8 px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            <nav className="text-xs text-foreground-400 mb-3 font-label">
              <Link to="/" className="hover:text-primary-500 cursor-pointer">Início</Link>
              <span className="mx-2">/</span>
              <span className="text-primary-500">Produtos</span>
            </nav>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">Produtos</h1>
            <p className="text-foreground-500 text-sm mt-2">Catálogo completo de materiais de construção</p>
          </div>
        </section>

        <PromoCarouselHome />

        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="w-full lg:w-72 shrink-0">
              <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden sticky top-24">
                <div className="bg-foreground-950 px-5 py-3">
                  <h2 className="text-background-50 font-label font-bold text-sm uppercase tracking-widest">Categorias</h2>
                </div>
                <button
                  onClick={() => { setActiveCategory(null); setActiveSubcategory(null); setExpandedCategory(null); }}
                  className={`w-full flex items-center px-5 py-3 text-sm font-label font-semibold transition-colors cursor-pointer border-b border-background-100 ${
                    !activeCategory ? "text-primary-600 bg-primary-50" : "text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  Todas as Categorias
                </button>
                <ul className="divide-y divide-background-100">
                  {categories.map((cat) => (
                    <li key={cat.id}>
                      <div
                        className={`flex items-center justify-between px-5 py-3.5 cursor-pointer transition-colors ${
                          activeCategory === cat.id ? "bg-background-100 text-foreground-950" : "text-foreground-700 hover:bg-background-50"
                        }`}
                        onClick={() => handleCategoryClick(cat.id)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-5 h-5 flex items-center justify-center text-foreground-400">
                            <i className={`${cat.icon} text-base`} />
                          </div>
                          <span className="text-sm font-label">{cat.name}</span>
                        </div>
                        <button onClick={(e) => toggleExpand(cat.id, e)} className="w-5 h-5 flex items-center justify-center text-foreground-400 hover:text-foreground-700 cursor-pointer">
                          <i className={`text-sm transition-transform duration-200 ${expandedCategory === cat.id ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`} />
                        </button>
                      </div>
                      {expandedCategory === cat.id && (
                        <ul className="bg-background-50 border-t border-background-100">
                          {cat.subcategories.map((sub) => (
                            <li key={sub.id}>
                              <button
                                onClick={() => { setActiveCategory(cat.id); setActiveSubcategory(sub.id); }}
                                className={`w-full text-left pl-12 pr-5 py-2.5 text-xs font-label transition-colors cursor-pointer ${
                                  activeSubcategory === sub.id ? "text-primary-600 bg-primary-50 font-semibold" : "text-foreground-600 hover:text-primary-500 hover:bg-background-100"
                                }`}
                              >
                                {sub.name}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                <p className="text-foreground-600 text-sm font-label">
                  <strong className="text-foreground-950">{filteredProducts.length}</strong> produto{filteredProducts.length !== 1 ? "s" : ""} encontrado{filteredProducts.length !== 1 ? "s" : ""}
                </p>
                <div className="relative w-full sm:w-72">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
                  <input
                    type="text"
                    placeholder="Buscar por nome, código, descrição, marca ou especificação..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 text-sm bg-background-50 border border-background-300 rounded-md font-label focus:outline-none focus:border-primary-400"
                  />
                </div>
              </div>

              {(activeCategory || activeSubcategory) && (
                <div className="flex flex-wrap items-center gap-2 mb-5">
                  {activeCategory && (() => {
                    const cat = categories.find((c) => c.id === activeCategory);
                    return cat ? (
                      <span className="flex items-center gap-1.5 bg-primary-100 text-primary-700 text-xs font-label font-semibold px-3 py-1 rounded-full">
                        <i className={`${cat.icon} text-xs`} />
                        {cat.name}
                        <button onClick={() => { setActiveCategory(null); setActiveSubcategory(null); }} className="ml-1 cursor-pointer hover:text-primary-900">
                          <i className="ri-close-line" />
                        </button>
                      </span>
                    ) : null;
                  })()}
                  {activeSubcategory && (() => {
                    const cat = categories.find((c) => c.id === activeCategory);
                    const sub = cat?.subcategories.find((s) => s.id === activeSubcategory);
                    return sub ? (
                      <span className="flex items-center gap-1.5 bg-background-200 text-foreground-700 text-xs font-label font-semibold px-3 py-1 rounded-full">
                        {sub.name}
                        <button onClick={() => setActiveSubcategory(null)} className="ml-1 cursor-pointer hover:text-foreground-900">
                          <i className="ri-close-line" />
                        </button>
                      </span>
                    ) : null;
                  })()}
                </div>
              )}

              {!activeCategory && !search ? (
                categories.map((cat) => {
                  const catProducts = products.filter((p) => p.category === cat.id);
                  if (catProducts.length === 0) return null;
                  return (
                    <div key={cat.id} className="mb-12">
                      <div className="flex items-center gap-3 mb-5">
                        <div className="w-8 h-8 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg">
                          <i className={`${cat.icon} text-base`} />
                        </div>
                        <h2 className="text-xl font-heading font-bold text-foreground-950">{cat.name}</h2>
                        <div className="flex-1 h-px bg-background-200" />
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4">
                        {catProducts.map((product) => (
                          <Link
                            key={product.id}
                            to={`/produto/${product.slug}`}
                            className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-all cursor-pointer"
                          >
                            <div className="relative h-36 overflow-hidden">
                              <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                              {product.promoLabel && (
                                <span className="absolute top-2 left-2 bg-primary-500 text-background-50 text-xs font-label font-bold px-2 py-0.5 rounded">{product.promoLabel}</span>
                              )}
                            </div>
                            <div className="p-3">
                              <p className="text-xs text-foreground-400 font-label">{product.brand}</p>
                              <p className="text-foreground-900 font-label font-semibold text-sm leading-tight mb-1 line-clamp-2">{product.name}</p>
                              {product.code && <span className="inline-flex items-center bg-primary-50 border border-primary-100 text-primary-700 text-[11px] font-label font-semibold px-2 py-0.5 rounded mt-1">Código: {product.code}</span>}
                              {product.showPrice ? (
                                <p className="text-primary-600 font-heading font-bold text-base">R$ {product.price.toFixed(2).replace(".", ",")}</p>
                              ) : (
                                <p className="text-foreground-400 text-xs italic">Consulte o preço</p>
                              )}
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                })
              ) : (
                <>
                  {filteredProducts.length === 0 ? (
                    <div className="text-center py-16">
                      <i className="ri-search-line text-5xl text-foreground-300 block mb-4" />
                      <p className="text-foreground-500 font-label">Nenhum produto encontrado.</p>
                      <button onClick={() => { setActiveCategory(null); setActiveSubcategory(null); setSearch(""); }} className="mt-4 text-primary-500 text-sm font-label underline cursor-pointer">
                        Limpar filtros
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4">
                      {filteredProducts.map((product) => (
                        <Link
                          key={product.id}
                          to={`/produto/${product.slug}`}
                          className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-all cursor-pointer"
                        >
                          <div className="relative h-36 overflow-hidden">
                            <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                            {product.promoLabel && (
                              <span className="absolute top-2 left-2 bg-primary-500 text-background-50 text-xs font-label font-bold px-2 py-0.5 rounded">{product.promoLabel}</span>
                            )}
                          </div>
                          <div className="p-3">
                            <p className="text-xs text-foreground-400 font-label">{product.brand}</p>
                            <p className="text-foreground-900 font-label font-semibold text-sm leading-tight mb-1 line-clamp-2">{product.name}</p>
                            {product.code && <span className="inline-flex items-center bg-primary-50 border border-primary-100 text-primary-700 text-[11px] font-label font-semibold px-2 py-0.5 rounded mt-1">Código: {product.code}</span>}
                            {product.showPrice ? (
                              <p className="text-primary-600 font-heading font-bold text-base">R$ {product.price.toFixed(2).replace(".", ",")}</p>
                            ) : (
                              <p className="text-foreground-400 text-xs italic">Consulte o preço</p>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <section className="bg-primary-500 py-14 px-4 text-center mt-8">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-background-50 mb-3">NOSSAS PROMOÇÕES</h2>
          <p className="text-primary-100 text-base mb-6 max-w-md mx-auto">Aproveite as melhores ofertas em materiais de construção!</p>
          <Link to="/promocoes" className="inline-flex items-center gap-2 bg-background-50 text-primary-600 hover:bg-background-100 px-8 py-3 rounded-md font-label font-bold text-sm cursor-pointer whitespace-nowrap transition-all">
            <i className="ri-price-tag-3-line" /> Ver Promoções
          </Link>
        </section>
      </main>
      <Footer />
    </>
  );
}